import writeXlsxFile, {
  type Cell,
  type SheetData,
} from 'write-excel-file/browser';
import { BuildContext, ExportOptions, ExportPayloadSection } from '../../types';
import reportProgressAndMaybeYield from '../reportProgressAndMaybeYield';
import resolveCellValue from '../resolveCellValue';
import resolveXlsxCellBoolean from './resolveXlsxCellBoolean';
import resolveXlsxCellNumber from './resolveXlsxCellNumber';
import stripControlChars from '../stripControlChars';
import resolveXlsxSheetName from './resolveXlsxSheetName';
import textRow from './textRow';

const buildXlsxWorkbook = async (
  sections: ExportPayloadSection[],
  options: ExportOptions,
  context: BuildContext
): Promise<Uint8Array> => {
  const showSectionLabels = sections.length > 1;
  const data: SheetData = [];

  if (options.header) {
    data.push(textRow(options.header));
  }

  for (const section of sections) {
    if (showSectionLabels) {
      data.push(textRow(section.label));
    }

    if (options.includeColumnNames) {
      data.push(
        section.columns.map<Cell>((column) => ({
          value: column.label,
          type: String,
          fontWeight: 'bold',
        }))
      );
    }

    for (const row of section.rows) {
      data.push(
        section.columns.map<Cell>((column) => {
          const rawValue = row[column.name];

          // An empty value stays an empty cell, without the '-' placeholder or a prefix/suffix.
          if (rawValue === null || rawValue === undefined || rawValue === '') {
            return { value: '', type: String, align: column.alignment };
          }

          const numericValue = resolveXlsxCellNumber(row, column, options);

          if (numericValue !== undefined) {
            return {
              value: numericValue,
              type: Number,
              align: column.alignment,
            };
          }

          const booleanValue = resolveXlsxCellBoolean(row, column, options);

          if (booleanValue !== undefined) {
            return {
              value: booleanValue,
              type: Boolean,
              align: column.alignment,
            };
          }

          return {
            value: stripControlChars(resolveCellValue(row, column, options)),
            type: String,
            align: column.alignment,
          };
        })
      );
      await reportProgressAndMaybeYield(context);
    }
  }

  if (options.footer) {
    data.push(textRow(options.footer));
  }

  const columnCount = sections.reduce(
    (max, section) => Math.max(max, section.columns.length),
    0
  );
  // Grid column min-widths (em) are reused as xlsx widths; xlsx measures column width in characters.
  const columns: { width?: number }[] = Array.from(
    { length: columnCount },
    (_, index) => {
      for (const section of sections) {
        const minWidth = section.columns[index]?.minWidth;
        if (typeof minWidth === 'number' && minWidth > 0) {
          // convert from em to char width
          return { width: minWidth * 2 };
        }
      }
      return {};
    }
  );
  const hasColumnWidths = columns.some((column) => column.width !== undefined);

  const blob = await writeXlsxFile(data, {
    sheet: resolveXlsxSheetName(sections),
    ...(hasColumnWidths ? { columns } : {}),
  }).toBlob();

  return new Uint8Array(await blob.arrayBuffer());
};

export default buildXlsxWorkbook;
